// userMessages — Blueticks' send queue + scheduled-message store.
//
// The collection is BOTH the public API's queue (POST /v1/messages
// writes here for `send_at` sends) AND the engine's send envelope
// (sendMessageThunkTool consumes the doc directly via
// paramsFromUserMessage). Same shape on both sides — this is the
// single source of truth.
//
// Status lifecycle (UserMessageStatusList — canonical, see ./message-status):
//   pending → confirmed → received → read → played
//                                       ↓
//                                     failed
//
// `LinkPreview` here is the PERSISTED form (uses `.url`, has many
// extra fields the extension keeps for re-rendering). NOT the same as
// the read-side `BtSlimLinkPreview` (wa/link-preview.ts) which uses
// WAWeb's `.canonicalUrl`+`matchedText` naming.

import { z } from 'zod';
import { WireJidSchema, WireKeySchema } from '../../contract/wire-identity';
import type { unitOfTime } from 'moment';
import type { NullableId } from '@feathersjs/feathers';
import {
  UserMessageStatusList,
  UserMessageStatusSchema,
  type UserMessageStatus,
} from './message-status';

// ── Enums ────────────────────────────────────────────────────────────

// Canonical vocabulary lives in ./message-status — single source of truth (the superset shared
// by the userMessages + messageLogs models, client UI, and public API). Re-exported under the
// generic `MessageStatus*` names so existing importers (and userMessages.model's `enum:`) are
// unaffected. Aliasing the already-imported UserMessageStatus* bindings keeps a real local binding
// (a bare `export { … } from …` would not, so the `MessageStatusSchema.optional()` use below needs it).
export const MessageStatusList = UserMessageStatusList;
export const MessageStatusSchema = UserMessageStatusSchema;
export type MessageStatus = UserMessageStatus;

export const SendingEngineList = ['whatsapp-web', 'gateway'] as const;
export const SendingEngineSchema = z.enum(SendingEngineList);
export type SendingEngine = z.infer<typeof SendingEngineSchema>;

/** #617 — where a user-message was created. Stamped once at creation (server-owned), analytics only;
 *  absent = unknown (docs before the stamp shipped are not backfilled). `agent` = a claude-agent reply. */
export const MessageOriginList = ['ui', 'api', 'mcp', 'campaign', 'recurring', 'agent'] as const;
export const MessageOriginSchema = z.enum(MessageOriginList);
export type MessageOrigin = z.infer<typeof MessageOriginSchema>;

/** Recurrence unit — matches moment's unitOfTime values that
 *  RRuleRecurrenceHandler + moment.diff/add operate on. Narrow enum
 *  here so consumers don't need to re-narrow before passing to moment.
 *  (bt-common doesn't import moment; just declares the value set.) */
export const RecurrenceUnitList = ['minute', 'hour', 'day', 'week', 'month', 'year'] as const;
export const RecurrenceUnitSchema = z.enum(RecurrenceUnitList);
export type RecurrenceUnit = z.infer<typeof RecurrenceUnitSchema>;

/** Retry-limit unit — currently only minutes/hours per the scheduler.
 *  Narrow union for moment.subtract() consumption. */
export const RetryLimitUnitList = ['minutes', 'hours'] as const;
export const RetryLimitUnitSchema = z.enum(RetryLimitUnitList);
export type RetryLimitUnit = z.infer<typeof RetryLimitUnitSchema>;

// ── Sub-shapes ───────────────────────────────────────────────────────

/**
 * Persisted link-preview on `userMessages.linkPreview`. The extension
 * reads `linkPreview.url` (see message-app-context.ts:54) — NOT
 * `canonicalUrl`. Extra fields mirror what unfurling libs produce
 * (siteName, mediaType, images, favicons, etc.) so the UI can re-render.
 *
 * Distinct from `BtSlimLinkPreview` (wa/link-preview.ts) — the slim
 * variant is the READ-side projection on incoming engine messages
 * using WAWeb's canonicalUrl naming.
 */
export const LinkPreviewSchema = z.object({
  url: z.string().optional(),
  link: z.string().optional(),
  title: z.string().optional(),
  siteName: z.string().optional(),
  description: z.string().optional(),
  mediaType: z.string().optional(),
  contentType: z.string().optional(),
  images: z.array(z.string()).optional(),
  videos: z.array(z.string()).optional(),
  favicons: z.array(z.string()).optional(),
  thumbnailUrl: z.string().optional(),
  thumbnail: z.string().optional(),
  thumbnailHQ: z.string().optional(),
  thumbnailWidth: z.number().optional(),
  thumbnailHeight: z.number().optional(),
  matchedText: z.string().optional(),
});
export type LinkPreview = z.infer<typeof LinkPreviewSchema>;

/** Entry of `contactList`. Engine reads `[0].id` as the WA JID.
 *  `_id` is Mongo's per-subdocument id (minted when the row is persisted;
 *  absent on freshly-built sends). Modeled like MessageAsset's `_id` —
 *  string | number | null — so `.lean()` docs satisfy the type and the
 *  v1 mapper can surface it on the public contactList. */
export const MessageContactSchema = z.object({
  id: WireJidSchema,
  contactId: z.string().optional(),
  name: z.string().optional(),
  formattedTitle: z.string().optional(),
  mobile: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  imageUrl: z.string().optional(),
  profilePicUrl: z.string().optional(),
  _id: z.union([z.string(), z.number(), z.null()]).optional(),
});
export type MessageContact = z.infer<typeof MessageContactSchema>;

/** Wire-format message key persisted on userMessages.waMessageKey once
 *  dispatch reaches WAWeb. Distinct from the engine-side
 *  BtSlimMessageKeySchema (wa/message-key.ts) which models the incoming
 *  engine wire form. This is the OUTBOUND/persisted form.
 *
 *  `remote` / `participant` are intentionally LOOSE (z.unknown) — legacy
 *  doc rows have varied shapes (wid object on older builds, string on
 *  newer ones, baileys' remoteJid alias). New rows should write strings;
 *  reads tolerate either. Tracked as a known carve-out from the
 *  "no ambiguous shapes" rule until a backfill migration normalizes
 *  historical rows. */
export const WAMessageKeySchema = z.object({
  fromMe: z.boolean().optional(),
  remote: z.unknown().optional(),
  id: z.string().optional(),
  _serialized: z.string().optional(),
  participant: z.unknown().optional(),
  remoteJid: z.string().optional(),
});
export type WAMessageKey = z.infer<typeof WAMessageKeySchema>;

/** Send-side / persisted poll. Options carry `{id, text, order}` —
 *  `id` is what the engine emits in the WA stanza for vote tracking,
 *  `order` is the render order. Distinct from `BtSlimPoll`
 *  (wa/poll.ts) — the slim read-side projection. */
export const PollSchema = z.object({
  question: z.string().optional(),
  options: z.array(z.object({
    id: z.number(),
    text: z.string(),
    order: z.number(),
  })).optional(),
  allowMultiple: z.boolean().optional(),
});
export type Poll = z.infer<typeof PollSchema>;

/**
 * An outbound reply to an interactive menu (a button / list row), blueticks-api#6 phase 5. The message
 * carries NO body: it is the selection, and `quotedMessageId` (REQUIRED, enforced by `prepareSend`) is the
 * menu it answers. `kind`/`title` are derived from the stored menu by the API (never trusted from the caller):
 *   buttons  -> buttonsResponseMessage{selectedButtonId: id, selectedDisplayText: title, type DISPLAY_TEXT}
 *   list     -> listResponseMessage{title, description, singleSelectReply{selectedRowId: id}}
 *   template -> templateButtonReplyMessage{selectedId: id, selectedDisplayText: title, selectedIndex: index}
 * It reads back as WA Web type `buttons_response` / `list_response` / `template_button_reply`.
 */
export const ButtonReplySchema = z.object({
  kind: z.enum(['buttons', 'list', 'template']),
  /** The option's id (buttonId / rowId / template button id). */
  id: z.string().min(1),
  /** The option's visible text (what the chat shows as our reply). */
  title: z.string().min(1),
  /** List rows only. */
  description: z.string().optional(),
  /** Template replies (and the option's position for buttons). */
  index: z.number().int().min(0).optional(),
});
export type ButtonReply = z.infer<typeof ButtonReplySchema>;

/** Send-asset descriptor. Engine reads `url`, `type`
 *  (BtPublicMediaKind — image / video / audio / voice / document /
 *  sticker), `name` for filename. `_id` is Feathers' NullableId for
 *  asset rows that exist as separate Mongo documents (legacy media path).
 *
 *  `url` is the SINGLE canonical field (#68) — for v1 sends it's the
 *  GCS-proxied URL the engine fetches from (v1 messages.service does
 *  the proxy step before persisting). For dashboard / campaign sends
 *  it's the caller's URL directly. Either way the engine has one URL
 *  to read. The caller's original URL for audit/trace lives in the
 *  request log, not on the persisted doc.
 *
 *  `type` should carry a BtPublicMediaKind value (#70) but the
 *  schema stays loose (`z.string()`) for chrome ext back-compat —
 *  many dashboard / scheduled-message UI sites still pass arbitrary
 *  strings (MIME hints, empty string for "auto-detect"). The v1
 *  mapper narrows to BtPublicMediaKind at the read boundary; values
 *  outside the enum surface as null on the public response. */
export const MessageAssetSchema = z.object({
  /** Display filename. Optional — the engine derives one from the URL
   *  when not supplied, and dashboard-created assets may omit it. */
  name: z.string().nullable().optional(),
  /** Media kind hint. When null/absent the engine auto-detects from
   *  URL or Content-Type. v1 narrows to BtPublicMediaKind at the
   *  mapper read boundary. */
  type: z.string().nullable().optional(),
  url: z.string(),
  /** Read-side flag for WhatsApp GIFs (video/mp4 sent with the GIF
   *  attribution). Surfaced by the Team Inbox mapper so the UI can
   *  autoplay/loop them in place instead of showing a play button.
   *  Optional — send-side callers never set it. */
  isGif: z.boolean().optional(),
  _id: z.union([z.string(), z.number(), z.null()]).optional(),
});
export type MessageAsset = z.infer<typeof MessageAssetSchema>;

/** #650 — most photos/videos one message sends as a single album. */
export const MAX_ALBUM_ITEMS = 10;
/** #650 — most attachments on one message (album items + audio/documents). */
export const MAX_MESSAGE_ASSETS = 15;

/** Calendar coloring spec. UI-only; engine ignores. */
export const CalendarColorsSchema = z.object({
  defaultColor: z.string(),
  specific: z.array(z.object({ messageTime: z.string(), color: z.string() })).optional(),
  ranges: z.array(z.object({ fromTime: z.string(), color: z.string() })).optional(),
});
export type CalendarColors = z.infer<typeof CalendarColorsSchema>;

// ── UserMessage doc ──────────────────────────────────────────────────

/**
 * userMessages collection row + send-thunk envelope. Engine consumers
 * (paramsFromUserMessage in extension/.../message-app-context.ts):
 *   - `contactList[0].id` → recipient JID
 *   - `message`           → text body
 *   - `asset`             → media descriptor
 *   - `poll` + `isPoll`   → poll spec
 *   - `linkPreview` + `sendLinkPreview` → attached preview
 *   - `quotedMessageId`   → reply target
 *
 * Backend lifecycle pipeline reads `dueDate`, `status`, `owner`, `api`
 * (channel marker), and the message-lifecycle timestamps (sentAt,
 * deliveredAt, readAt, failedAt — written by message-logs but spread
 * into the doc shape by the mapper for single-source consumption).
 *
 * `null` on `message` / `asset` / `poll` / `rruleset` / `linkPreview`
 * indicates explicit clear (Mongoose $set null unsets the field; used
 * when switching variants on patch — #82).
 *
 * `owner` / `campaignId` accept Feathers' NullableId (string | number |
 * null) — the userMessages collection persists ObjectId-as-string but
 * Feathers' generic typing allows the framework value to flow through.
 * The mapper coerces to string at the boundary.
 *
 * `.passthrough()` on the OUTER schema allows runtime parse to admit
 * Mongoose's internal markers (__v, _doc, etc.) without rejecting.
 * The exported `UserMessage` TYPE is strict (`z.infer<>` without
 * `& Record<string, any>`) — strict at compile time, lenient at
 * runtime is the right asymmetry. Consumers reading undocumented
 * fields will surface as tsc errors at the callsite (fix at source).
 */
export const UserMessageSchema = z.object({
  // Identity / tenancy. `_id` is required — a UserMessage that hasn't
  // been persisted yet is `Partial<UserMessage>` at the construction
  // site, not a `UserMessage`. Every read path + the send-thunk
  // boundary always has `_id`.
  _id: z.string(),
  /** Mongoose's `.id` virtual — `_id.toString()`. Some consumers read
   *  the virtual instead of _id; surfaced here so .lean() docs satisfy
   *  the type even though the virtual technically requires a Document
   *  instance to evaluate. */
  id: z.string().optional(),
  tempId: z.string().optional(),
  owner: z.union([z.string(), z.number(), z.null()]).optional(),
  campaignId: z.union([z.string(), z.number(), z.null()]).optional(),
  deleted: z.boolean().optional(),
  expires: z.coerce.date().optional(),
  status: MessageStatusSchema.optional(),
  sentWith: SendingEngineSchema.optional(),
  // Chrome ext's WhatsappSendMessageThunk writes the WAWeb key here
  // on dispatch (and the real-time ack handler updates it on later
  // matches). Chrome UI reads it back for ack/status display. The
  // v1 mapper sources its public `key` from messageLogs.waMessageKey
  // via the lifecycle input (#69) — this field stays for the chrome
  // dispatch/UI loop, not for v1's read path.
  /** Stored documents still hold both shapes (object from older writers, wire key
   *  string from 5.9a on), so this stays WAMessageKeySchema until the backfill
   *  count reads 0. @5.9b WireKeySchema (with the reader collapse). */
  waMessageKey: WAMessageKeySchema.optional(),
  retryCount: z.number().optional(),
  contactName: z.string().optional(),

  // Scheduling
  dueDate: z.coerce.date().optional(),
  dueTime: z.coerce.date().optional(),

  /** Sender JID — echoed on the public API response when supplied by
   *  the caller (multi-session sends pick the from-engine). */
  from: z.string().nullable().optional(),

  /** Caller-supplied opaque correlation id, kept on the document and echoed on
   *  the public API. When it fits (≤ 28 bytes) it is also the wire messageSecret
   *  token instead of `_id` (see `resolveWireSecret`). */
  secret: z.string().optional(),

  // Body variants — exactly one populated per row.
  message: z.string().nullable().optional(),
  asset: MessageAssetSchema.nullable().optional(),
  /** #650 — multi-media message, in send order. Photos/videos (2+) go out as one
   *  WhatsApp album; `message` is the caption of the first item. Writers keep
   *  `asset` = `assets[0]` so readers that only know `asset` still see the first
   *  item. Absent / fewer than 2 items = a plain single-asset message. */
  assets: z.array(MessageAssetSchema).max(MAX_MESSAGE_ASSETS).optional(),
  contactList: z.array(MessageContactSchema).optional(),
  /** Wire key string of the reply target (`<fromMe>_<remote>_<id>[_<participant>]`). */
  quotedMessageId: WireKeySchema.optional(),
  // BE#253 — participant JIDs to @mention in a group message. Engines set the
  // outgoing message's contextInfo.mentionedJid from this: wa-js maps it to
  // `mentionedJidList` (prepareMessageForWa), Baileys passes it as
  // `{ mentions }` on sock.sendMessage. Applies to text and media captions.
  // Additive to (does not replace) the inline `@[Display](jid)` markup wa-js
  // already parses out of the body.
  mentions: z.array(WireJidSchema).optional(),

  // Behavior flags
  useSpintax: z.boolean().optional(),
  cancelIfReceived: z.boolean().optional(),
  isRecurring: z.boolean().optional(),
  verifyContact: z.boolean().optional(),
  callbackUrl: z.string().optional(),
  clientIdempotencyKey: z.string().optional(),
  /** FE#447 — REQUEST-ONLY: the creating client's own clock (epoch ms) + UTC offset (minutes east) on a
   *  create/update from a UI. The server turns it into `creatorClockSkewMs` and never stores it. */
  clientNowMs: z.number().optional(),
  clientTzOffsetMin: z.number().optional(),
  /** FE#447 — the creator's clock skew (creatorClock − serverClock, ms) when this message was saved.
   *  dueDate is on the CREATOR's timeline; the server-timeline instant is dueDate − this. Absent/0 for
   *  server-side creators (API, campaigns) — recurring successors inherit their parent's. */
  creatorClockSkewMs: z.number().optional(),
  api: z.boolean().optional(),
  /** #617 — creation origin; see MessageOriginList. */
  origin: MessageOriginSchema.optional(),
  /** #922 S0b — the sender (`wa_senders._id`) this message is bound to. Server-owned, stamped at create;
   *  absent = the owner's default sender (all docs created before S0b). */
  senderId: z.string().optional(),
  state: z.number().optional(),

  // Recurrence
  recurrenceQuantity: z.number().optional(),
  recurenceUnit: RecurrenceUnitSchema.optional(),
  rruleset: z.string().nullable().optional(),

  // Attached link preview
  linkPreview: LinkPreviewSchema.nullable().optional(),
  sendLinkPreview: z.boolean().optional(),

  // UI metadata
  messageColor: z.string().optional(),
  calendarColors: CalendarColorsSchema.optional(),

  // Retry
  retryLimitUnit: RetryLimitUnitSchema.optional(),
  retryLimitDuration: z.number().optional(),

  // Reply target — the WA message key of the message being replied to.
  // Modeled as WAMessageKeySchema since that's what consumers narrow it to
  // (`replyData?._serialized || replyData?.id`).
  replyData: WAMessageKeySchema.optional(),
  // Coerced because MQTT/JSON wires Date as an ISO string — z.date() rejects
  // strings, which 114a's strict args-parse at the dispatcher boundary made
  // visible as `Expected date, received string` 422s on every send.
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),

  // Counts surfaced on the calendar-sidebar message row. Persisted —
  // backend writes these from a per-contact aggregation when listing
  // scheduled messages.
  contactWAId: z.string().optional(),
  contactImageUrl: z.string().nullable().optional(),
  messageCount: z.number().optional(),
  messageScheduledCount: z.number().optional(),
  messageApiCount: z.number().optional(),
  messageCampaignCount: z.number().optional(),

  // Image asset reference — id of an Image row attached to this message
  // (legacy media path that pre-dates `asset.url`). Surface only on rows
  // sent through the scheduled-message editor.
  imageId: z.string().nullable().optional(),

  // Poll
  isPoll: z.boolean().optional(),
  poll: PollSchema.nullable().optional(),

  /** Reply to an interactive menu (exclusive with message/asset/poll; needs `quotedMessageId`). */
  buttonReply: ButtonReplySchema.nullable().optional(),

  // Ack
  ack: z.number().optional(),
  ackData: z.unknown().optional(),

  // Rate-limit / send-slot surfacing (§1). The server-authoritative send-slot gate
  // (acquire-send-slot) records its throttle decision onto the doc so the calendar + WhatsApp UI —
  // which read the usermessage, not Redis — can show a first-class "rate limited" state.
  //   - `lastSlotReason` is STICKY at 'backoff' once a message is throttled (a grant clears only the
  //     live window below, not this marker) so a throttle-caused terminal is distinguishable.
  //   - `backoffUntil` is the epoch when the current throttle lifts; set on a backoff deny, unset
  //     (null) on grant. The display keys "currently throttled" off this being in the future.
  // Values mirror acquire-send-slot's SlotReason (kept in sync by hand — only 'backoff'/'granted'
  // are ever written; the rest are modeled for faithfulness). bt-common must not import the backend.
  lastSlotReason: z.enum([
    'granted', 'backoff', 'exhausted', 'window_expired',
    'not_found', 'already_terminal', 'empty_destination', 'gateway_preferred',
  ]).optional(),
  backoffUntil: z.coerce.date().nullable().optional(),

  // Failure tracking
  sendFailureReason: z.string().optional(),
  lastSendAttempt: z.coerce.date().optional(),
  // #98: stamped once, on the FIRST actual send attempt (never overwritten). Used to bound
  // re-dispatch of an already-sent-but-stuck message without dropping a not-yet-sent (lagging)
  // one — see isMessageIsWithinSendingLimits.
  firstSendAttempt: z.coerce.date().optional(),
  sendFailureError: z.unknown().optional(),
  retryLockTimestamp: z.coerce.date().optional(),
  retryLockedBy: z.string().optional(),

  // WhatsApp client state at send time
  whatsappState: z.string().optional(),

  // BaseEntity fields (mirrored here so UserMessage doesn't extend a
  // schema whose `.passthrough()` would re-introduce an index signature).
  // When BaseEntity gets its own zod migration, replace with
  // `BaseEntitySchema.extend({...})`.
  version: z.number().optional(),
  dirtySessionId: z.string().optional(),
  dirty: z.boolean().optional(),

  // Mongoose's optimistic-concurrency version key. Persisted on every
  // doc; `.lean()` includes it. Modeled so strict parse doesn't reject.
  __v: z.number().optional(),
});

/**
 * Strict schema, strict type. No `.passthrough()`, no escape hatch.
 * Every field a consumer reads MUST be modeled above; the runtime parse
 * rejects undocumented extras (per "no ambiguous shapes" memory). When
 * a consumer wants UI-only state, it layers a separate type over
 * UserMessage on its own side — those fields never make it into the
 * persisted/canonical shape.
 */
export type UserMessage = z.infer<typeof UserMessageSchema>;

// Re-export NullableId / unitOfTime for type compat with the legacy
// interface file's importers. Keeps the surface 1:1.
export type { NullableId, unitOfTime };

/**
 * Coerce a wire-string / number / Date / null / undefined into a Date.
 * Returns undefined for nullish inputs. Used by UI/thunk code that
 * constructs UserMessage objects from upstream results where the
 * source type is non-canonical (RRule helpers, wire strings from
 * lookup endpoints, etc). The schema itself uses z.coerce.date() at
 * the dispatcher boundary; this helper is its TS-typed twin for code
 * that builds the object structurally before parsing.
 */
export function toUserMessageDate(input: string | number | Date | null | undefined): Date | undefined {
  if (input === null || input === undefined || input === '') return undefined;
  return new Date(input);
}
